import { DentalSpecialty, DisplayType } from '~/types/specialty';

export type ProcedureOption = {
  label: string;
  value: string;
};

export type ProcedureGroup = {
  name: string;
  options: ProcedureOption[];
  displayType: DisplayType;
  /** UI heading rendered above the group; falls back to name. */
  heading?: string;
  /** When true, a value must be selected before the referral can be submitted. */
  required?: boolean;
  /** When true, the selected option folds into one summary line with reason + teeth. */
  inlineSummary?: boolean;
};

export type SpecialtyReason = {
  label: string;
  value: string;
  procedures?: ProcedureGroup[];
};

export type SpecialtyReasons = {
  [key: string]: SpecialtyReason[];
};

/** Mandatory hospital-admission treatment plan shown inside every HOSPITAL reason (#1169). */
export const HOSPITAL_TREATMENT_PLAN_GROUP: ProcedureGroup = {
  name: 'Treatment Plan',
  heading: 'Choose a Treatment Plan',
  displayType: DisplayType.BUTTONS,
  required: true,
  inlineSummary: true,
  options: [
    { label: 'Dental Extractions', value: 'dental_extractions' },
    { label: 'Wisdom Teeth Removal', value: 'wisdom_teeth_removal' },
    { label: 'Full Mouth Dental Extractions', value: 'full_mouth_dental_extractions' },
  ],
};

export const specialtyReasons: SpecialtyReasons = {
  [DentalSpecialty.ORAL_SURGERY]: [
    { label: 'Dental Extraction', value: 'dental_extraction' },
    { label: 'Wisdom Teeth Removal', value: 'wisdom_teeth_removal' },
    { label: 'Implant Consultation', value: 'implant_consultation' },
    { label: 'Full Mouth Dental Extractions', value: 'full_mouth_extractions' },
    { label: 'Full Arch Rehabilitation with Implants Consultation', value: 'full_arch_implants' },
    { label: 'Sinus Lift', value: 'sinus_lift' },
    { label: 'Peri-Implantitis / Peri-Gingivitis', value: 'peri_implantitis' },
    { label: 'Trauma', value: 'trauma' },
    {
      label: 'Pathology',
      value: 'oral_surgery_pathology',
      procedures: [
        {
          name: 'Pathology Procedures',
          displayType: DisplayType.DROPDOWN,
          options: [
            { label: 'Biopsy', value: 'biopsy' },
            { label: 'Head / Neck Lesion', value: 'head_neck_lesion' },
          ],
        },
      ],
    },
    {
      label: 'Pre-Prosthodontic Services',
      value: 'pre_prosthodontic_services',
      procedures: [
        {
          name: 'Pre-Prosthodontic Procedures',
          displayType: DisplayType.DROPDOWN,
          options: [
            { label: 'Ridge Augmentation', value: 'ridge_augmentation' },
            { label: 'Alveoloplasty', value: 'alveoloplasty' },
            { label: 'Bone Graft', value: 'bone_graft' },
            { label: 'Sinus Augmentation', value: 'sinus_augmentation' },
          ],
        },
      ],
    },
    { label: 'Expose & Bond', value: 'surgery_expose_and_bond' },
    {
      label: 'Orthodontic Services',
      value: 'orthodontic_services',
      procedures: [
        {
          name: 'Orthodontic Procedures',
          displayType: DisplayType.DROPDOWN,
          options: [
            { label: 'Expose and Bond', value: 'expose_and_bond' },
            { label: 'Orthnognathic Evaluation', value: 'orthnognathic_evaluation' },
            { label: 'Cleft Lip / Cleft Palate', value: 'cleft_lip_palate' },
            { label: 'Impacted Canine', value: 'impacted_canine' },
          ],
        },
      ],
    },
    { label: 'Other', value: 'other' },
  ],
  [DentalSpecialty.PERIODONTAL]: [
    {
      label: 'Complete Periodontal Evaluation',
      value: 'complete_perio_eval',
      procedures: [
        {
          name: 'Surgical Procedures',
          displayType: DisplayType.CHECKBOX,
          options: [
            { label: 'Gingival Recession', value: 'gingival_recession' },
            { label: 'Deep Probing Depth', value: 'deep_probing_depth' },
            { label: 'Grafting for Root Coverage', value: 'grafting_for_root_coverage' },
            { label: 'Atraumatic Extraction', value: 'atraumatic_extraction' },
            { label: 'Implant Therapy', value: 'implant_therapy' },
            { label: 'Regenerative Procedures', value: 'regenerative_procedures' },
            { label: 'Crown Lengthening', value: 'crown_lengthening' },
            { label: 'Frenum Removal', value: 'frenum_removal' },
            { label: 'Sinus Augmentation', value: 'sinus_augmentation' },
            { label: 'Vestibuloplasty', value: 'vestibuloplasty' },
            { label: 'Gummy Smile', value: 'gummy_smile' },
            { label: 'Laser Treatment', value: 'laser_treatment' },
            { label: 'Implant Site Development', value: 'implant_site_development' },
            { label: 'Other', value: 'other' },
          ],
        },
        {
          name: 'Non-Surgical Options',
          displayType: DisplayType.DROPDOWN,
          options: [
            { label: 'Scaling and Root Planing', value: 'scaling_and_root_planing' },
            { label: 'Other', value: 'other' },
          ],
        },
      ],
    },
    {
      label: 'Limited Periodontal Evaluation',
      value: 'limited_perio_eval',
      procedures: [
        {
          name: 'Surgical Procedures',
          displayType: DisplayType.CHECKBOX,
          options: [
            { label: 'Gingival Recession', value: 'gingival_recession' },
            { label: 'Deep Probing Depth', value: 'deep_probing_depth' },
            { label: 'Grafting for Root Coverage', value: 'grafting_for_root_coverage' },
            { label: 'Atraumatic Extraction', value: 'atraumatic_extraction' },
            { label: 'Implant Therapy', value: 'implant_therapy' },
            { label: 'Regenerative Procedures', value: 'regenerative_procedures' },
            { label: 'Crown Lengthening', value: 'crown_lengthening' },
            { label: 'Frenum Removal', value: 'frenum_removal' },
            { label: 'Sinus Augmentation', value: 'sinus_augmentation' },
            { label: 'Vestibuloplasty', value: 'vestibuloplasty' },
            { label: 'Gummy Smile', value: 'gummy_smile' },
            { label: 'Laser Treatment', value: 'laser_treatment' },
            { label: 'Implant Site Development', value: 'implant_site_development' },
            { label: 'Other', value: 'other' },
          ],
        },
        {
          name: 'Non-Surgical Options',
          displayType: DisplayType.DROPDOWN,
          options: [
            { label: 'Scaling and Root Planing', value: 'scaling_and_root_planing' },
            { label: 'Other', value: 'other' },
          ],
        },
      ],
    },
  ],
  [DentalSpecialty.ENDODONTIC]: [
    { label: 'NSRCT (Non Surgical Root Canal Treatment)', value: 'nsrct' },
    { label: 'Endodontic Retreatment', value: 'endo_retreatment' },
    { label: 'Broken/Separated Instrument', value: 'broken_instrument' },
    { label: 'Diagnose and Treat', value: 'diagnose_and_treat' },
    { label: 'Internal / External Resorption', value: 'resorption' },
    { label: 'Cracked Tooth', value: 'cracked_tooth' },
    { label: 'Apicoectomy', value: 'apicoectomy' },
    { label: 'Trauma', value: 'trauma' },
    { label: 'Internal Bleaching', value: 'internal_bleaching' },
    { label: 'Extract and Place Implant', value: 'extract_and_implant' },
  ],
  [DentalSpecialty.ORTHODONTIC]: [
    { label: 'Braces', value: 'braces' },
    { label: 'Invisalign', value: 'invisalign' },
    { label: 'Pre-Prosthetic orthodontic treatment', value: 'pre_prosthetic_ortho' },
    { label: 'Pre-Implant Treatment', value: 'pre_implant_treatment' },
    { label: 'Impacted Teeth Consultation', value: 'impacted_teeth_consult' },
    { label: 'Orthognathic Surgery', value: 'orthognathic_surgery' },
    { label: 'Revision Orthodontics', value: 'revision_orthodontics' },
  ],
  [DentalSpecialty.TMJ]: [
    { label: 'TMJ / jaw pain', value: 'tmj_jaw_pain' },
    { label: 'Limited opening or locking', value: 'limited_opening_or_locking' },
    { label: 'Suspected sleep apnea', value: 'suspected_sleep_apnea' },
    { label: 'CPAP intolerance', value: 'cpap_intolerance' },
    { label: 'Second opinion', value: 'second_opinion' },
  ],
  [DentalSpecialty.SLEEP]: [
    { label: 'Sleep Apnea Treatment', value: 'sleep_apnea' },
    { label: 'Oral Appliance Therapy', value: 'oral_appliance' },
    { label: 'Snoring Treatment', value: 'snoring' },
  ],
  [DentalSpecialty.PEDIATRICS]: [
    { label: 'Restorative Treatment', value: 'restorative_treatment' },
    { label: 'Carious Management', value: 'carious_management' },
    { label: 'Stainless Steel Crowns', value: 'stainless_steel_crowns' },
    { label: 'Second Opinion', value: 'second_opinion' },
    { label: 'Intellectual Developmental Disorder (Special Needs)', value: 'special_needs' },
    { label: 'Trauma', value: 'trauma' },
    { label: 'Behavior Management', value: 'behavior_management' },
    { label: 'Orthodontic Treatment', value: 'orthodontic_treatment' },
    { label: 'Space Maintenance', value: 'space_maintenance' },
    { label: 'Oral Pathology', value: 'oral_pathology' },
    { label: 'Habit Therapy', value: 'habit_therapy' },
    { label: 'Sedation', value: 'sedation' },
    { label: 'Dental Extraction', value: 'dental_extraction' },
  ],
  [DentalSpecialty.RADIOLOGY]: [
    { label: 'Advanced Imaging', value: 'advanced_imaging' },
    { label: 'CBCT Scan', value: 'cbct_scan' },
    { label: 'Diagnostic Consultation', value: 'diagnostic_consult' },
  ],
  [DentalSpecialty.HOSPITAL]: [
    {
      label: 'Dental Anxiety',
      value: 'dental_anxiety',
      procedures: [HOSPITAL_TREATMENT_PLAN_GROUP],
    },
    { label: 'High BMI', value: 'high_bmi', procedures: [HOSPITAL_TREATMENT_PLAN_GROUP] },
    {
      label: 'Intellectual Developmental Disorder',
      value: 'intellectual_developmental_disorder',
      procedures: [HOSPITAL_TREATMENT_PLAN_GROUP],
    },
    {
      label: 'Substance Abuse',
      value: 'substance_abuse',
      procedures: [HOSPITAL_TREATMENT_PLAN_GROUP],
    },
    {
      label: 'Complex Medical History',
      value: 'complex_medical_history',
      procedures: [HOSPITAL_TREATMENT_PLAN_GROUP],
    },
  ],
  [DentalSpecialty.PATHOLOGY]: [
    { label: 'Infection', value: 'infection' },
    { label: 'Salivary Gland Pathology', value: 'salivary_gland_pathology' },
    { label: 'Oral Complications from Cancer Therapy', value: 'cancer_therapy_complications' },
    { label: 'Evaluate Oral Lesion', value: 'evaluate_oral_lesion' },
    { label: 'Pigmentation', value: 'pigmentation' },
    { label: 'Ulcer', value: 'ulcer' },
    { label: 'Nodule', value: 'nodule' },
    { label: 'Oral Cancer Examination / Evaluation', value: 'oral_cancer_evaluation' },
    { label: 'Oral Facial Pain', value: 'oral_facial_pain' },
  ],
  [DentalSpecialty.PROSTHODONTIC]: [
    { label: 'Complete Denture', value: 'complete_denture' },
    { label: 'Partial Dentures', value: 'partial_dentures' },
    { label: 'Immediate Dentures', value: 'immediate_dentures' },
    { label: 'Overdentures', value: 'overdentures' },
    { label: 'Full Arch Implant Bridge/Hybrid', value: 'full_arch_implant_bridge' },
    { label: 'TMD Evaluation', value: 'tmd_evaluation' },
    { label: 'Full Arch Rehabilitation', value: 'full_arch_rehabilitation' },
    { label: 'Implant Treatment', value: 'implant_treatment' },
  ],
  [DentalSpecialty.GENERAL_DENTISTRY]: [
    { label: 'Crown Recementation', value: 'crown_recementation' },
    { label: 'Teeth Whitening', value: 'teeth_whitening' },
    { label: 'New Patient Dental Exam and Dental Cleaning', value: 'new_patient_exam_cleaning' },
    { label: 'Second Opinion', value: 'second_opinion' },
    { label: 'Implant Restoration', value: 'implant_restoration' },
    { label: 'Sleep Appliance', value: 'sleep_appliance' },
  ],
};

export function getFlattenedReasons(): SpecialtyReason[] {
  return Object.values(specialtyReasons).flat();
}
